package com.milta.feature.imports.data.remote

import com.milta.core.network.ApiException
import com.milta.core.network.Endpoints
import com.milta.core.network.baseUrl
import com.milta.feature.auth.data.remote.dto.ApiError
import com.milta.feature.imports.data.remote.dto.ImportResultDto
import com.milta.feature.imports.domain.entity.ImportType
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.forms.formData
import io.ktor.client.request.forms.submitFormWithBinaryData
import io.ktor.client.statement.HttpResponse
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess

class ImportApi(private val client: HttpClient) {

    /** POST /api/v1/imports — uploads the picked file for AI extraction (DOM-02, écran A). */
    suspend fun startImport(fileBytes: ByteArray, fileName: String, importType: ImportType): ImportResultDto =
        client.submitFormWithBinaryData(
            url = "$baseUrl/${Endpoints.imports}",
            formData = formData {
                append("importType", importType.wireValue())
                append(
                    key = "file",
                    value = fileBytes,
                    headers = Headers.build {
                        append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                    },
                )
            },
        ).decodeOrThrow()

    /** POST /api/v1/imports/{importSessionId}/retry — re-uploads a file for the same session (DOM-02, écrans C/D). */
    suspend fun retryImport(importSessionId: String, fileBytes: ByteArray, fileName: String): ImportResultDto =
        client.submitFormWithBinaryData(
            url = "$baseUrl/${Endpoints.importRetry(importSessionId)}",
            formData = formData {
                append(
                    key = "file",
                    value = fileBytes,
                    headers = Headers.build {
                        append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                    },
                )
            },
        ).decodeOrThrow()

    private suspend inline fun <reified T> HttpResponse.decodeOrThrow(): T {
        if (!status.isSuccess()) {
            val apiError = runCatching { body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${status.value}"
            throw ApiException(status.value, message)
        }
        return body()
    }
}

/** `importType` request field — mirrors the backend `ImportType.ToString()` PascalCase (see [ImportType]'s own `@SerialName`s). */
private fun ImportType.wireValue(): String = when (this) {
    ImportType.PHOTO -> "Photo"
    ImportType.PDF -> "Pdf"
    ImportType.SPREADSHEET -> "Spreadsheet"
}
